import React, {useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Table from 'shared/migration-components/table';
import QueryDeleteButton from 'shared/migration-components/query-delete-button';
import {AdditionalProps} from 'modules/additionals/types';
import useAdditionalGroupAdditionals from 'modules/additional-groups/hooks/use-additional-group-additionals';
import toBrl from 'shared/util/to-brl';
import additionalGroupsKeys from 'modules/additional-groups/util/additional-groups-keys';
import ApiDynamicSwitch from 'shared/migration-components/api-dynamic-switch';
import useDisableAdditionalGroupAdditionals from 'modules/additional-groups/hooks/use-disable-additional-group-additionals';
import IconButton from 'shared/migration-components/icon-button';
import {IoPower} from 'react-icons/io5';
import styled from 'styled-components';
import useReorderAdditionalGroupAdditionalsIndexes from 'modules/additional-groups/hooks/use-reorder-additional-group-additionals-indexes';

const Additionals: React.FC = () => {
  const {id} = useParams();
  const [search, setSearch] = useState('');
  const {data = [], isLoading} = useAdditionalGroupAdditionals(id!);
  const navigate = useNavigate();
  const disableAdditionalGroupAdditionalsMutation =
    useDisableAdditionalGroupAdditionals(id!);

  const reorderAdditionalGroupAdditionalsIndexes =
    useReorderAdditionalGroupAdditionalsIndexes(id!);

  const additionals = useMemo(
    () =>
      data.filter(
        (additional) =>
          additional.name
            .toLocaleLowerCase()
            .search(search ? search.toLocaleLowerCase() : search) >= 0,
      ),
    [data, search],
  );

  return (
    <Container>
      <Header>
        <p>Mostrando um total de {additionals.length} resultados encontrados</p>
        <Row>
          <IconButton
            icon={<IoPower />}
            label="Desativar adicionais"
            loading={disableAdditionalGroupAdditionalsMutation.isLoading}
            onClick={() =>
              disableAdditionalGroupAdditionalsMutation.mutate(undefined)
            }
          />
          <SearchInput
            placeholder="Pesquisar..."
            onChange={(e) => setSearch(e.target.value)}
          />
        </Row>
      </Header>
      <Table<AdditionalProps>
        draggable
        onDrag={(fromId, toId) =>
          reorderAdditionalGroupAdditionalsIndexes.mutate({
            fromId,
            toId,
          })
        }
        loading={isLoading}
        data={additionals}
        options={[
          'name',
          {value: 'price', column: (row) => toBrl(row.price ?? 0)},
          'description',
          {
            value: 'active',
            column: {
              value: (row) => (
                <ApiDynamicSwitch
                  isChecked={row.active}
                  keys={{updateList: additionalGroupsKeys.additionals(id!)}}
                  request={{
                    method: 'PATCH',
                    url: `/additionals/${row.id}`,
                    property: 'active',
                    errorTitle: 'Erro ao alterar status do adicional',
                  }}
                  multipart
                />
              ),
              stopPropagation: true,
            },
          },
          {
            value: 'id',
            label: 'Ações',
            column: (row) => (
              <QueryDeleteButton
                url={`/additionals/${row.id}`}
                queryKey={additionalGroupsKeys.additionals(id!)}
                type="update"
              />
            ),
          },
        ]}
        onRowClick={(row) => navigate(row.id)}
        translatedPropsKey="AdditionalProps"
      />
    </Container>
  );
};

export default Additionals;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 10px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  p {
    font-size: 12px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const SearchInput = styled.input`
  width: 150px;
  height: 30px;
  background-color: transparent;
  border-radius: 4px;
  padding: 0px 5px;
  border: 1px solid rgba(128, 128, 128, 0.8);
  margin-top: -3px;
`;
